import {
  feedbackListResponseSchema,
  type FeedbackListResponse,
  type FeedbackFilters,
  type Feedback,
} from '../types';
import {
  authFetch, API_BASE_URL 
} from '@/lib/api-client';

// Mock data for development
const mockFeedback: FeedbackListResponse = {
  data: [
    {
      feedbackId: 'fb-001',
      employeeId: 'emp-101',
      employeeName: 'Maria Garcia',
      employeeRole: 'Line Cook',
      siteId: 'site-001',
      siteName: 'Main Kitchen',
      type: 'praise',
      category: 'Food Quality',
      title: 'Excellent dish presentation',
      content: 'Maria consistently delivers beautifully plated dishes that exceed guest expectations. Her attention to detail on the lobster thermidor has received multiple compliments.',
      rating: 5,
      submittedBy: 'user-001',
      submittedByName: 'Chef Martinez',
      status: 'acknowledged',
      createdAt: '2026-01-28T14:30:00Z',
      acknowledgedAt: '2026-01-29T09:15:00Z',
    },
    {
      feedbackId: 'fb-002',
      employeeId: 'emp-102',
      employeeName: 'James Wilson',
      employeeRole: 'Storekeeper',
      siteId: 'site-001',
      siteName: 'Main Kitchen',
      type: 'recognition',
      category: 'Inventory Management',
      title: 'Zero stock-outs this month',
      content: 'James has maintained perfect inventory levels for 30 consecutive days. His proactive reordering and organization have significantly improved kitchen operations.',
      rating: 5,
      submittedBy: 'user-002',
      submittedByName: 'Sarah Chen',
      status: 'submitted',
      createdAt: '2026-01-27T10:00:00Z',
      acknowledgedAt: null,
    },
    {
      feedbackId: 'fb-003',
      employeeId: 'emp-103',
      employeeName: 'David Kim',
      employeeRole: 'Receiving Clerk',
      siteId: 'site-002',
      siteName: 'Deck 9 Buffet',
      type: 'coaching',
      category: 'Quality Control',
      title: 'Temperature logging improvement needed',
      content: 'David needs additional training on proper temperature logging procedures. Recommend scheduling a refresher session on cold chain compliance.',
      rating: 3,
      submittedBy: 'user-001',
      submittedByName: 'Chef Martinez',
      status: 'submitted',
      createdAt: '2026-01-26T16:45:00Z',
      acknowledgedAt: null,
    },
    {
      feedbackId: 'fb-004',
      employeeId: 'emp-104',
      employeeName: 'Emily Brown',
      employeeRole: 'Sous Chef',
      siteId: 'site-001',
      siteName: 'Main Kitchen',
      type: 'improvement',
      category: 'Communication',
      title: 'Shift handover documentation',
      content: 'Emily should improve her shift handover notes. Key prep items were missed due to incomplete documentation. Suggest using the standard handover template.',
      rating: 2,
      submittedBy: 'user-003',
      submittedByName: 'Mike Johnson',
      status: 'acknowledged',
      createdAt: '2026-01-25T08:30:00Z',
      acknowledgedAt: '2026-01-25T14:00:00Z',
    },
    {
      feedbackId: 'fb-005',
      employeeId: 'emp-101',
      employeeName: 'Maria Garcia',
      employeeRole: 'Line Cook',
      siteId: 'site-001',
      siteName: 'Main Kitchen',
      type: 'praise',
      category: 'Teamwork',
      title: 'Great mentorship of new staff',
      content: 'Maria has been exceptional in training our new line cooks. Her patience and clear instruction have accelerated their onboarding significantly.',
      rating: 5,
      submittedBy: 'user-002',
      submittedByName: 'Sarah Chen',
      status: 'submitted',
      createdAt: '2026-01-24T11:20:00Z',
      acknowledgedAt: null,
    },
  ],
  pagination: {
    nextToken: null,
    totalCount: 5,
  },
};

export async function getFeedbackList(
  filters?: FeedbackFilters
): Promise<FeedbackListResponse> {
  // In production, this would call the API
  if (API_BASE_URL) {
    const params = new URLSearchParams();
    if (filters?.employeeId) params.set('employeeId', filters.employeeId);
    if (filters?.type) params.set('type', filters.type);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.siteId) params.set('siteId', filters.siteId);
    if (filters?.search) params.set('search', filters.search);

    const queryString = params.toString();
    const url = queryString
      ? `${API_BASE_URL}/v1/performance/feedback?${queryString}`
      : `${API_BASE_URL}/v1/performance/feedback`;

    const response = await authFetch(url);

    if (!response.ok) {
      throw new ApiClientError(response.status, `Failed to fetch feedback: ${response.statusText}`);
    }

    const data: unknown = await response.json();
    return feedbackListResponseSchema.parse(data);
  }

  // Return filtered mock data for development
  await new Promise((resolve) => setTimeout(resolve, 500));
  
  const filtered = filterFeedbackData(mockFeedback.data, filters);

  return {
    data: filtered,
    pagination: {
      nextToken: null,
      totalCount: filtered.length 
    },
  };
}

function filterFeedbackData(data: Feedback[], filters?: FeedbackFilters): Feedback[] {
  return data.filter((f) => {
    if (filters?.type && f.type !== filters.type) return false;
    if (filters?.status && f.status !== filters.status) return false;
    if (filters?.search) {
      const search = filters.search.toLowerCase();
      const matchesSearch = 
        f.employeeName.toLowerCase().includes(search) ||
        f.title.toLowerCase().includes(search) ||
        f.content.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }
    return true;
  });
}

export class ApiClientError extends Error {
  constructor(
    public readonly statusCode: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}
